"use client";

import { useState, useRef, useEffect } from "react";
import Badge from '@/app/components/atoms/Badge';
import InfoBanner from '@/app/components/atoms/InfoBanner';
import TextLink from '@/app/components/atoms/TextLink';
import type { VisitData } from '@/app/components/types';

/**
 * @component VisitItem
 * @description Single booked visit entry showing event name, date/time, optional note, and tag.
 * Long notes are clamped to 3 lines with a "Show more" toggle.
 * @param name - Name of the event or activity.
 * @param date - Display date (e.g. "August 16").
 * @param time - Display time range (e.g. "10:30 — 11:30AM").
 * @param note - Optional informational note shown below the visit.
 * @param href - Optional URL to make the event name clickable (opens in new tab).
 * @param tag - Optional tag label (e.g. "Members Only").
 * @returns An `<article>` element with the visit details.
 */
export default function VisitItem({ name, date, time, note, href, tag }: VisitData) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const clampedRef = useRef<HTMLParagraphElement>(null);
  const measureRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const clamped = clampedRef.current;
    const full = measureRef.current;
    if (!clamped || !full) return;

    // full is visually-hidden (h-px overflow-hidden) so offsetHeight is always 1px,
    // but scrollHeight reflects the natural unclamped text height — works on Safari/iOS
    // where scrollHeight on a line-clamped element doesn't reflect the visual clamp.
    let cancelled = false;
    const check = () => {
      if (!cancelled) setOverflows(full.scrollHeight > clamped.offsetHeight);
    };

    // Wait for fonts to finish loading before measuring — custom fonts affect line
    // height and text wrapping, so measuring before they load gives wrong results.
    // Then defer by one rAF to catch the post-font layout paint.
    (document.fonts?.ready ?? Promise.resolve()).then(() => requestAnimationFrame(check));

    const observer = new ResizeObserver(check);
    observer.observe(clamped);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [note]);

  return (
    <article className="py-6">
      <div className="flex items-start">
        {href ? (
          <TextLink as="a" href={href} target="_blank" rel="noopener noreferrer" size="lg" className="underline">
            {name}
          </TextLink>
        ) : (
          <span className="text-body-semibold text-charcoal">{name}</span>
        )}
        <div className="text-right min-w-0 ml-auto">
          <time className="text-body-base text-charcoal block">
            {date}
          </time>
          <div className="text-secondary-base text-stone">{time}</div>
        </div>
      </div>

      {note && (
        <InfoBanner className="mt-3">
          {/* relative wrapper gives the absolute twin the correct width — InfoBanner
              has no positioned ancestor so without this, width:100% would resolve to
              a wider parent and text would wrap less, making scrollHeight falsely small. */}
          <div className="relative w-full">
            <p
              ref={measureRef}
              aria-hidden="true"
              className="absolute w-full h-px p-0 -m-px overflow-hidden border-0"
            >
              {note}
            </p>
            <p ref={clampedRef} className={!expanded ? "line-clamp-3" : ""}>
              {note}
            </p>
            {(overflows || expanded) && (
              <div className="flex justify-end -mt-1">
                <TextLink onClick={() => setExpanded((p) => !p)}>
                  {expanded ? "Show less" : "Show more"}
                </TextLink>
              </div>
            )}
          </div>
        </InfoBanner>
      )}

      {tag && <Badge label={tag} />}
    </article>
  );
}
